'use client'

import * as React from 'react'
import { SidebarInset, useSidebar, SidebarTrigger } from '@/components/ui/sidebar'
import { useContainerWidth } from '@/hooks/use-container-width'

interface MainContentProps {
    children: React.ReactNode
    scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>
    rightSidebarContext: React.ReactNode
}

/**
 * Main content area component that handles the scrollable content region.
 * Extracted from app-layout for better code organization.
 */
export function MainContent({ scrollContainerRef, rightSidebarContext }: MainContentProps) {
    const { state: sidebarState } = useSidebar()
    const { containerWidth, containerRef } = useContainerWidth()

    // Calculate padding class based on container width
    const paddingClass = React.useMemo(() => {
        if (!containerWidth) return 'px-5' // Default

        if (containerWidth < 500) return 'px-0'
        if (containerWidth < 700) return 'px-1'
        if (containerWidth < 900) return 'px-5'
        if (containerWidth < 1100) return 'px-7'
        if (containerWidth < 1300) return 'px-8'
        return 'px-20'
    }, [containerWidth])

    return (
        <SidebarInset className="flex flex-1 flex-col min-w-0 bg-sidebar overflow-hidden h-full">
            {/* Sticky SidebarTrigger at top left */}
            <div className="fixed top-0 left-1 z-50 p-2">
                {sidebarState === 'collapsed' && <SidebarTrigger className="-ml-1" />}
            </div>

            <div
                ref={(node) => {
                    containerRef(node)
                    scrollContainerRef.current = node
                }}
                className={`flex-1 px-5 overflow-y-auto ${paddingClass} bg-background dark:bg-black h-[calc(100vh-3.125rem)] min-h-0 scrollbar-none w-full`}
            >
                {/* Spacer element that provides top spacing and scrolls away naturally */}
                <div className="h-10 flex-shrink-0" />
                {rightSidebarContext}
            </div>
        </SidebarInset>
    )
}
